import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import './AuthForm.css';
import Sidebar from './Sidebar';
import ProfileMenu from './ProfileMenu';
import './Main.css';
import MobileHamburger from './MobileHamburger';
import MobileMenu from './MobileMenu';
import { useNavigate } from 'react-router-dom';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import {FaPen, FaPlus, FaTrash, FaArrowUp, FaArrowDown} from 'react-icons/fa6';
import Preloader from './Preloader';

// Define libraries outside of component to prevent recreation
const libraries = ['places', 'marker'];

function Reports() {
  const [incidents, setIncidents] = useState([]);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedIncidents, setSelectedIncidents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    type: '',
    location: '',
    details: '',
    image_url: '',
    location_coords: null,
    severity: 'low',
    status: 'active'
  });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const navigate = useNavigate();
  const autocompleteRef = useRef(null);
  const itemsPerPage = 10;
  const [page, setPage] = useState(1);
  const [sidebarNarrow, setSidebarNarrow] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: "AIzaSyAugo7vMiL-tP9dyz6mlCvcZqcovWJl1eA",
    libraries
  });

  useEffect(() => {
      document.title = "Incident Reports";
    }, []);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('user_id', user.id)
          .single();
        setCurrentUser(profile);
      }
    };
    getCurrentUser();

    const incidentReportsChannel = supabase
      .channel('public:incident_reports_page') // Unique channel name
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'incident_reports' },
        (payload) => {
          console.log('Incident report change received!', payload);
          fetchIncidents();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(incidentReportsChannel);
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchIncidents();
    }
  }, [currentUser]);

  async function fetchIncidents() {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      let query = supabase
        .from('incident_reports')
        .select('*');
      
      if (search) {
        query = query.ilike('location', `%${search}%`);
      }
      if (typeFilter) {
        query = query.eq('type', typeFilter);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching incidents:', error);
        setIncidents([]);
      } else {
        setIncidents(data || []);
      }
    } catch (error) {
      console.error('Error in fetchIncidents:', error);
      setIncidents([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleImageUpload(event) {
    const file = event.target.files[0];
    if (file) {
      try {
        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
          alert('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
          return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB in bytes
        if (file.size > maxSize) {
          alert('Image size should be less than 5MB');
          return;
        }

        setImageFile(file);

        // Generate a unique file name using timestamp and random string
        const timestamp = new Date().getTime();
        const randomString = Math.random().toString(36).substring(7);
        const fileExt = file.name.split('.').pop().toLowerCase();
        const fileName = `${timestamp}-${randomString}.${fileExt}`;

        // Check if user is authenticated
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          alert('You must be logged in to upload images');
          return;
        }

        const filePath = `19m4n2r_0/${fileName}`;

        // Upload to Supabase storage with proper content type
        const { error: uploadError } = await supabase.storage
          .from('incident-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false,
            contentType: file.type
          });

        if (uploadError) {
          console.error('Error uploading image:', uploadError);
          if (uploadError.message.includes('row-level security policy')) {
            alert('You do not have permission to upload images. Please contact an administrator.');
          } else {
            alert('Failed to upload image. Please try again.');
          }
          return;
        }

        // Get the public URL of the uploaded image
        const { data: { publicUrl } } = supabase.storage
          .from('incident-images')
          .getPublicUrl(filePath);

        // Update the form state with the new image URL
        setForm(prev => ({
          ...prev,
          image_url: publicUrl
        }));

      } catch (error) {
        console.error('Error in handleImageUpload:', error);
        alert('Failed to upload image. Please try again.');
      }
    }
  }

  async function handleArchiveSelected() {
    if (selectedIncidents.length === 0) {
      alert('Please select incidents to archive');
      return;
    }

    try {
      const { error } = await supabase
        .from('incident_reports')
        .update({ status: 'archived' })
        .in('id', selectedIncidents);

      if (error) throw error;
      // Audit log for archive
      for (const id of selectedIncidents) {
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'archived incident report',
          target_id: id,
          target_type: 'IncidentReports',
          details: { status: 'archived' }
        }]);
      }
      alert('Selected incidents archived successfully!');
      setSelectedIncidents([]);
      fetchIncidents();
    } catch (error) {
      console.error('Error archiving incidents:', error);
      alert(`Failed to archive incidents: ${error.message}`);
    }
  }

  function toggleIncidentSelection(id) {
    setSelectedIncidents(prev => 
      prev.includes(id) 
        ? prev.filter(incidentId => incidentId !== id)
        : [...prev, id]
    );
  }

  function openForm(incident = null) {
    if (incident) {
      setForm({
        type: (incident.type || '').toLowerCase(),
        location: incident.location || '',
        details: incident.details || '',
        image_url: incident.image_url || '',
        location_coords: incident.location_coords || null,
        severity: incident.severity || 'low',
        status: incident.status || 'active'
      });
      setEditId(incident.id);
    } else {
      setForm({
        type: '',
        location: '',
        details: '',
        image_url: '',
        location_coords: null,
        severity: 'low',
        status: 'active'
      });
      setEditId(null);
    }
    setShowForm(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    try {
      const incidentData = {
        type: form.type.charAt(0).toUpperCase() + form.type.slice(1),
        location: form.location,
        details: form.details,
        image_url: form.image_url,
        location_coords: form.location_coords,
        severity: form.severity,
        status: form.status,
        user_id: currentUser.id
      };

      if (editId) {
        // Update existing incident
        const { error } = await supabase
          .from('incident_reports')
          .update(incidentData)
          .eq('id', editId);

        if (error) throw error;
        // Audit log for edit
        await supabase.from('audit_logs').insert([{
          user_id: currentUser.id,
          action: 'edited incident report',
          target_id: editId,
          target_type: 'IncidentReports',
          details: incidentData
        }]);
        alert('Incident updated successfully!');
      } else {
        // Insert new incident
        const { data: insertData, error } = await supabase
          .from('incident_reports')
          .insert([incidentData])
          .select();

        if (error) throw error;
        // Audit log for create
        if (insertData && insertData.length > 0) {
          await supabase.from('audit_logs').insert([{
            user_id: currentUser.id,
            action: 'created incident report',
            target_id: insertData[0].id,
            target_type: 'IncidentReports',
            details: incidentData
          }]);
        }
        alert('Incident report added successfully!');
      }

      setShowForm(false);
      fetchIncidents();
    } catch (error) {
      console.error('Error saving incident:', error);
      alert(`Failed to save incident: ${error.message || 'Unknown error occurred'}`);
    } finally {
      setLoading(false);
    }
  }

  function handleRowClick(id, event) {
    // Check if the click was on the edit button or its children
    if (event.target.closest('button')) {
      return;
    }
    navigate(`/incident-reports/${id}`);
  }

  const onPlaceSelected = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.geometry) {
        setForm(prev => ({
          ...prev,
          location: place.formatted_address,
          location_coords: {
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng()
          }
        }));
      }
    }
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          // Reverse geocode to get the address
          const geocoder = new window.google.maps.Geocoder();
          try {
            const result = await geocoder.geocode({
              location: { lat: latitude, lng: longitude }
            });
            
            if (result.results[0]) {
              setForm(prev => ({
                ...prev,
                location: result.results[0].formatted_address,
                location_coords: {
                  latitude,
                  longitude
                }
              }));
            }
          } catch (error) {
            console.error('Error getting address:', error);
            alert('Error getting address from coordinates');
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Error getting your current location');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser');
    }
  };

  // Filter incidents in-memory for case-insensitive search and type filter
  const filteredIncidents = incidents.filter(incident => {
    const matchesSearch =
      !search ||
      (incident.location && incident.location.toLowerCase().includes(search.toLowerCase()));
    let matchesType = true;
    if (typeFilter === 'archived') {
      matchesType = incident.status === 'archived';
    } else if (typeFilter) {
      matchesType = incident.type && incident.type.toLowerCase() === typeFilter.toLowerCase();
    }
    return matchesSearch && matchesType;
  });

  const totalPages = Math.ceil(filteredIncidents.length / itemsPerPage);
  const paginatedIncidents = filteredIncidents.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  if (!currentUser) {
    return <Preloader text="Loading Reports..." />;
  }

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f4f8f8' }}>
      <Sidebar onNarrowChange={setSidebarNarrow}/>
      <div style={{ flex: 1, padding: 32, width: '100%', overflowX: 'auto' }}>
        <div className="header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Mobile Hamburger Menu */}
            <MobileHamburger
              isOpen={showMobileMenu}
              onToggle={() => setShowMobileMenu(prev => !prev)}
            />
            <div className="title-text">Incident Reports</div>
          </div>
          <ProfileMenu />
        </div>

        {/* Mobile Dropdown Menu */}
        <MobileMenu 
          isOpen={showMobileMenu} 
          onClose={() => setShowMobileMenu(false)} 
        />
        
        {/* Reports Section */}
        <div>
          <div className="search-actions-row">
            <div className="search-filters">
              <input
                type="text"
                placeholder="Search by location"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="search-input"
              />
              <select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                className="type-filter"
              >
                <option value="">All Types</option>
                <option value="fire">Fire</option>
                <option value="flood">Flood</option>
                <option value="medical">Medical</option>
                <option value="rescue">Rescue</option>
                <option value="others">Others</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="action-buttons">
              <button
                onClick={() => {
                  setEditId(null);
                  setForm({
                    type: '',
                    location: '',
                    details: '',
                    image_url: '',
                    location_coords: null,
                    severity: 'low',
                    status: 'active'
                  });
                  setShowForm(true);
                }}
                className="create-button"
              >
                <FaPlus /> Create Report
              </button>
              <button
                onClick={handleArchiveSelected}
                disabled={selectedIncidents.length === 0}
                className="archive-button"
              >
                <FaTrash /> Archive Selected
              </button>
            </div>
          </div>

          <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px #0001', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1100 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                  <th style={{ padding: 12 }}>
                    <input
                      type="checkbox"
                      checked={selectedIncidents.length === incidents.length && incidents.length > 0}
                      onChange={e =>
                        setSelectedIncidents(
                          e.target.checked ? incidents.map(i => i.id) : []
                        )
                      }
                    />
                  </th>
                  <th style={{ padding: 12 }}>Image</th>
                  <th style={{ padding: 12, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Type</th>
                  <th style={{ padding: 12, maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Address</th>
                  <th style={{ padding: 12, maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Details</th>
                  <th style={{ padding: 12, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Severity</th>
                  <th style={{ padding: 12 }}>Status</th>
                  <th style={{ padding: 12 }}>Votes</th>
                  <th style={{ padding: 12 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedIncidents.length > 0 ? (
                  paginatedIncidents.map(incident => (
                    <tr
                      key={incident.id}
                      style={{
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onClick={e => handleRowClick(incident.id, e)}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f8f9fa')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <td style={{ padding: 12 }}>
                        <input
                          type="checkbox"
                          checked={selectedIncidents.includes(incident.id)}
                          onChange={e => {
                            e.stopPropagation();
                            toggleIncidentSelection(incident.id);
                          }}
                          onClick={e => e.stopPropagation()}
                        />
                      </td>
                      <td style={{ padding: 12 }}>
                        <img 
                          src={incident.image_url || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+Tm8gSW1hZ2U8L3RleHQ+Cjwvc3ZnPgo='} 
                          alt={incident.type}
                          style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8 }}
                        />
                      </td>
                      <td style={{ padding: 12, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: 12,
                          fontWeight: 600,
                          fontSize: 14,
                          background: (incident.type && incident.type.toLowerCase() === 'fire') ? '#ffe0b2' :
                                     (incident.type && incident.type.toLowerCase() === 'flood') ? '#bbdefb' :
                                     (incident.type && incident.type.toLowerCase() === 'medical') ? '#ffcdd2' :
                                     (incident.type && incident.type.toLowerCase() === 'rescue') ? '#c8e6c9' :
                                     '#fff9c4',
                          color: (incident.type && incident.type.toLowerCase() === 'fire') ? '#e65100' :
                                 (incident.type && incident.type.toLowerCase() === 'flood') ? '#1976d2' :
                                 (incident.type && incident.type.toLowerCase() === 'medical') ? '#d32f2f' :
                                 (incident.type && incident.type.toLowerCase() === 'rescue') ? '#388e3c' :
                                 '#fbc02d',
                          border: '1px solid #eee',
                          minWidth: 70,
                          textAlign: 'center',
                          maxWidth: 120,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>{incident.type ? incident.type.charAt(0).toUpperCase() + incident.type.slice(1) : '-'}</span>
                      </td>
                      <td style={{ padding: 12, maxWidth: 180, position: 'relative' }}>
                        <div style={{
                          maxWidth: 180,
                          maxHeight: 48,
                          overflow: 'hidden',
                          position: 'relative',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          whiteSpace: 'normal',
                          textOverflow: 'ellipsis',
                          lineHeight: '1.2em',
                          background: 'linear-gradient(to bottom, rgba(255,255,255,0) 60%, #fff 100%)'
                        }}>{incident.location}</div>
                      </td>
                      <td style={{ padding: 12, maxWidth: 300, position: 'relative' }}>
                        <div style={{
                          maxWidth: 300,
                          maxHeight: 48,
                          overflow: 'hidden',
                          position: 'relative',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          whiteSpace: 'normal',
                          textOverflow: 'ellipsis',
                          lineHeight: '1.2em',
                          background: 'linear-gradient(to bottom, rgba(255,255,255,0) 60%, #fff 100%)'
                        }}>{incident.details}</div>
                      </td>
                      <td style={{ padding: 12, maxWidth: 120, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: 12,
                          fontWeight: 600,
                          fontSize: 14,
                          background: incident.severity === 'critical' ? '#ffebee' : incident.severity === 'high' ? '#fff3e0' : incident.severity === 'medium' ? '#e3f2fd' : '#e8f5e9',
                          color: incident.severity === 'critical' ? '#c62828' : incident.severity === 'high' ? '#ef6c00' : incident.severity === 'medium' ? '#1976d2' : '#388e3c',
                          border: '1px solid #eee',
                          minWidth: 70,
                          textAlign: 'center',
                          maxWidth: 120,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>{incident.severity ? incident.severity.charAt(0).toUpperCase() + incident.severity.slice(1) : '-'}</span>
                      </td>
                      <td style={{ padding: 12 }}>
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: 12,
                          fontWeight: 600,
                          fontSize: 14,
                          background:
                            incident.status === 'active' ? '#fdecea' :
                            incident.status === 'responding' ? '#fff4e5' :
                            incident.status === 'resolved' ? '#e6f4ea' :
                            '#f5f5f5',
                          color:
                            incident.status === 'active' ? '#d32f2f' :
                            incident.status === 'responding' ? '#f59e0b' :
                            incident.status === 'resolved' ? '#2e7d32' :
                            '#616161',
                          border: '1px solid #eee',
                          minWidth: 70,
                          textAlign: 'center'
                        }}>{incident.status ? incident.status.charAt(0).toUpperCase() + incident.status.slice(1) : '-'}</span>
                      </td>
                      <td style={{ padding: 12, textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#19c37d', fontWeight: 600 }}>
                            <FaArrowUp /> {incident.upvotes_count ?? 0}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#e74c3c', fontWeight: 600 }}>
                            <FaArrowDown /> {incident.downvotes_count ?? 0}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2 border text-gray-700">
                        <div className="flex justify-center items-center space-x-2">
                          {currentUser && (currentUser.role === 'admin' || currentUser.role === 'super admin' || (currentUser.role === 'barangay' && incident.user_id === currentUser.id)) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openForm(incident);
                              }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22 }}
                            >
                              <FaPen />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#888' }}>No incident reports found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, gap: 8 }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === 1 ? 'not-allowed' : 'pointer' }}
              >
                Previous
              </button>
              <div style={{ display: 'flex', gap: 4 }}>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setPage(i + 1)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      cursor: 'pointer',
                      background: page === i + 1 ? '#08b1c7' : '#fff',
                      color: page === i + 1 ? '#fff' : '#333',
                      fontWeight: page === i + 1 ? 'bold' : 'normal'
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{ background: '#eee', border: 'none', borderRadius: 4, padding: '4px 12px', cursor: page === totalPages ? 'not-allowed' : 'pointer' }}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.5)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: 24,
                width: '90%',
                maxWidth: 600,
                height: 700,
                boxShadow: '0 4px 32px rgba(8, 177, 199, 0.18)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 24,
                  flexShrink: 0
                }}
              >
                <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>
                  {editId ? 'Edit Report' : 'Create New Report'}
                </h2>
                <button
                  onClick={() => setShowForm(false)}
                  type="button"
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 28,
                    cursor: 'pointer',
                    color: '#888'
                  }}
                >
                  ×
                </button>
              </div>
              <form
                onSubmit={handleSubmit}
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Type</label>
                  <select
                    value={form.type}
                    onChange={e => setForm(prev => ({ ...prev, type: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: 12,
                      borderRadius: 6,
                      border: '1px solid #ccc',
                      fontSize: 16,
                      marginBottom: 0,
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="">Select Type</option>
                    <option value="fire">Fire</option>
                    <option value="flood">Flood</option>
                    <option value="medical">Medical</option>
                    <option value="rescue">Rescue</option>
                    <option value="others">Others</option>
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Location</label>
                  {isLoaded ? (
                    <Autocomplete
                      onLoad={autocomplete => {
                        autocompleteRef.current = autocomplete;
                      }}
                      onPlaceChanged={onPlaceSelected}
                      restrictions={{ country: 'ph' }}
                    >
                      <input
                        type="text"
                        value={form.location}
                        onChange={e => setForm(prev => ({ ...prev, location: e.target.value }))}
                        required
                        placeholder="Search for a location"
                        style={{
                          width: '100%',
                          maxWidth: '100%',
                          padding: 12,
                          borderRadius: 6,
                          border: '1px solid #ccc',
                          fontSize: 16,
                          marginBottom: 0,
                          boxSizing: 'border-box'
                        }}
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      type="text"
                      value={form.location}
                      onChange={e => setForm(prev => ({ ...prev, location: e.target.value }))}
                      required
                      placeholder="Search for a location"
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        padding: 12,
                        borderRadius: 6,
                        border: '1px solid #ccc',
                        fontSize: 16,
                        marginBottom: 0,
                        boxSizing: 'border-box'
                      }}
                    />
                  )}
                  <button
                    type="button"
                    style={{
                      marginTop: 8,
                      border: '1px solid #19c37d',
                      background: '#e6fff4',
                      color: '#19c37d',
                      padding: '10px 20px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                    onClick={handleUseCurrentLocation}
                  >
                    Use Current Location
                  </button>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Details</label>
                  <textarea
                    value={form.details}
                    onChange={e => setForm(prev => ({ ...prev, details: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      minHeight: 100,
                      maxHeight: 500,
                      padding: 12,
                      borderRadius: 6,
                      border: '1px solid #ccc',
                      fontSize: 16,
                      marginBottom: 0,
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Severity</label>
                  <select
                    value={form.severity}
                    onChange={e => setForm(prev => ({ ...prev, severity: e.target.value }))}
                    required
                    style={{
                      width: '100%',
                      maxWidth: '100%',
                      padding: 12,
                      borderRadius: 6,
                      border: '1px solid #ccc',
                      fontSize: 16,
                      marginBottom: 0,
                      boxSizing: 'border-box'
                    }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </div>
                {editId && (
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', marginBottom: 8 }}>Status</label>
                    <select
                      value={form.status}
                      onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}
                      style={{
                        width: '100%',
                        maxWidth: '100%',
                        padding: 12,
                        borderRadius: 6,
                        border: '1px solid #ccc',
                        fontSize: 16,
                        marginBottom: 0,
                        boxSizing: 'border-box'
                      }}
                    >
                      <option value="active">Active</option>
                      <option value="responding">Responding</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                )}
                <div style={{ marginBottom: 16 }}>
                  <label
                    htmlFor="incident-image-upload"
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px',
                      background: '#fff',
                      color: '#19c37d',
                      border: '1px solid #19c37d',
                      borderRadius: 8,
                      cursor: 'pointer',
                      marginBottom: 8
                    }}
                  >
                    Upload Image
                  </label>
                  <input
                    id="incident-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  {form.image_url && (
                    <img
                      src={form.image_url}
                      alt="Preview"
                      style={{
                        width: '100%',
                        height: 200,
                        objectFit: 'cover',
                        marginTop: 8,
                        borderRadius: 4
                      }}
                    />
                  )}
                </div>
              </form>
              {/* Modal Footer (fixed at bottom, outside scrollable area) */}
                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    marginTop: 24,
                    flexShrink: 0,
                    background: '#fff'
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    disabled={loading}
                    style={{
                      padding: '12px 24px',
                      borderRadius: 8,
                      border: '1px solid #ccc',
                      background: '#fff',
                      color: '#333',
                      fontSize: 16,
                      fontWeight: 'bold',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="incident-form" // Optional: if you want to link to the form
                    disabled={loading}
                    style={{
                      padding: '12px 24px',
                      borderRadius: 8,
                      border: 'none',
                      background: loading ? '#ccc' : '#19c37d',
                      color: '#fff',
                      fontSize: 16,
                      fontWeight: 'bold',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                    onClick={() => document.querySelector('form').requestSubmit()} // Ensures submit works from outside form
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Reports;